import ArrowsPointingOutIcon from '@heroicons/react/24/outline/ArrowsPointingOutIcon';
import DocumentIcon from '@heroicons/react/24/outline/DocumentIcon';
import ExclamationCircleIcon from '@heroicons/react/24/outline/ExclamationCircleIcon';
import ExclamationTriangleIcon from '@heroicons/react/24/outline/ExclamationTriangleIcon';
import PencilIcon from '@heroicons/react/24/outline/PencilIcon';
import TrashIcon from '@heroicons/react/24/outline/TrashIcon';
import escape from 'lodash/escape';
import { CSSProperties, useEffect, useMemo } from 'react';

import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Checkbox } from '@/components/core/Checkbox';
import { Icon } from '@/components/core/Icon';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/core/Table';
import { Tooltip } from '@/components/core/Tooltip';
import { formatBytes } from '@/components/core/Uploader/utils';
import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { useValidationTranslator } from '@/components/datamodel/hooks/useValidationTranslator';
import { useFormatAttributeValue } from '@/components/datamodel/utils/formatAttributeValue';
import { useTranslation } from '@/hooks/useTranslation';
import { type DamUploadFile, hasValidationErrors, hasValidationWarnings, hasValue } from '@/pages/dam/utils/damUploadFiles';
import type { DataModelAttribute, PersistedItem } from '@/types/DataModel';

type DamUploadListMode = 'link' | 'data' | 'review';

interface DamUploadFileListProps {
    files: DamUploadFile[];
    mode: DamUploadListMode;
    /** `asset_upload` attributes, the columns of the data and review modes. */
    attributes: DataModelAttribute[];
    /** One-line description of a linked product (product name, GTIN, target market). */
    describeItem: (item: PersistedItem) => string;
    selectedIds?: Set<string>;
    onSelectedChange?: (ids: string[], selected: boolean) => void;
    onEdit?: (file: DamUploadFile) => void;
    onDelete?: (file: DamUploadFile) => void;
    onZoom?: (file: DamUploadFile) => void;
}

/** `core/Table` draws a line between all cells; the wireframe list separates rows only. */
const tableStyle = { '--table-border': 'none' } as CSSProperties;

const EMPTY_VALUE = '–';

const FileThumbnail = ({ file }: { file: File }) => {
    const url = useMemo(() => (file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined), [file]);
    useEffect(() => () => {
        if (url) {
            URL.revokeObjectURL(url);
        }
    }, [url]);

    return (
        <div className="tw-flex tw-h-16 tw-w-16 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded tw-border tw-border-solid tw-border-tertiary tw-bg-fill-primary">
            {url ? <img src={url} alt="" className="tw-h-full tw-w-full tw-object-contain" /> : <Icon icon={DocumentIcon} size="lg" variant="lighter-gray" />}
        </div>
    );
};

/** Error or warning symbol behind the file name, the messages in its tooltip (D-051). */
const ValidationStatus = ({ file }: { file: DamUploadFile }) => {
    const { t } = useTranslation();
    const { translateValidationMessage } = useValidationTranslator();
    const isError = hasValidationErrors(file);
    if (!isError && !hasValidationWarnings(file)) {
        return null;
    }
    const messages = Object.entries({ ...file.validation?.warnings, ...file.validation?.errors })
        .flatMap(([attributeName, entries]) => entries.map((entry) => escape(translateValidationMessage(entry, attributeName))));
    const label = t(isError ? 'DAM.UPLOAD.TAB_ERRORS' : 'DAM.UPLOAD.TAB_WARNINGS');
    return (
        <Tooltip popupContent={messages.join('<br>')}>
            <span role="img" aria-label={label} className="tw-flex">
                <Icon icon={isError ? ExclamationCircleIcon : ExclamationTriangleIcon} size="sm" variant={isError ? 'destructive' : 'warning'} />
            </span>
        </Tooltip>
    );
};

/**
 * File list of the upload wizard, steps 2 to 4 (wireframe rows `7537:57862;7535:290886` ff.,
 * `7328:237429;7536:293964` ff., review `7328:237403;7536:294449` ff.): thumbnail, name, size and
 * pixel size; "Linked products" in the link and review modes, the `asset_upload` attributes in the
 * data and review modes. Link and data modes add selection and row actions.
 */
function DamUploadFileList({
    files,
    mode,
    attributes,
    describeItem,
    selectedIds = new Set(),
    onSelectedChange,
    onEdit,
    onDelete,
    onZoom,
}: DamUploadFileListProps) {
    const { t } = useTranslation();
    const { formatValue } = useFormatAttributeValue();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const isEditable = mode !== 'review';
    const showProducts = mode !== 'data';
    const showAttributes = mode !== 'link';
    const allSelected = files.length > 0 && files.every((file) => selectedIds.has(file.id));
    const someSelected = files.some((file) => selectedIds.has(file.id));

    const valueOf = (attribute: DataModelAttribute, value: unknown) => {
        if (!hasValue(value)) {
            return EMPTY_VALUE;
        }
        const display = formatValue(attribute, value);
        return display && display !== '-' ? display : EMPTY_VALUE;
    };

    return (
        <Table style={tableStyle} containerClassName="tw-overflow-x-auto">
            <TableHeader>
                <TableRow className="tw-bg-fill-secondary">
                    {isEditable && (
                        <TableHead className="tw-w-10 tw-px-2">
                            <Checkbox
                                aria-label={t('DAM.UPLOAD.SELECT_ALL')}
                                isChecked={allSelected}
                                isIndeterminate={someSelected && !allSelected}
                                onCheckedChange={() => onSelectedChange?.(files.map((file) => file.id), !allSelected)}
                            />
                        </TableHead>
                    )}
                    <TableHead>{t('DAM.UPLOAD.FILES')}</TableHead>
                    {showProducts && <TableHead>{t('DAM.UPLOAD.LINKED_PRODUCTS')}</TableHead>}
                    {showAttributes && attributes.map((attribute) => (
                        <TableHead key={attribute.name}>{translateAttributeLabel(attribute)}</TableHead>
                    ))}
                    {isEditable && <TableHead className="tw-text-right">{t('DAM.UPLOAD.ACTIONS')}</TableHead>}
                </TableRow>
            </TableHeader>
            <TableBody>
                {files.map((file) => {
                    const [firstItem, ...moreItems] = file.items;
                    const size = [formatBytes(file.file.size), file.dimensions && `${file.dimensions.width} × ${file.dimensions.height}px`]
                        .filter(Boolean)
                        .join(' · ');
                    return (
                        <TableRow key={file.id} className="tw-bg-fill-primary" selected={selectedIds.has(file.id)}>
                            {isEditable && (
                                <TableCell className="tw-w-10 tw-px-2">
                                    <Checkbox
                                        aria-label={file.file.name}
                                        isChecked={selectedIds.has(file.id)}
                                        onCheckedChange={(checked) => onSelectedChange?.([file.id], checked === true)}
                                    />
                                </TableCell>
                            )}
                            <TableCell className="tw-py-3">
                                <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
                                    <FileThumbnail file={file.file} />
                                    <div className="tw-flex tw-min-w-0 tw-flex-col">
                                        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                                            <span className="tw-max-w-xs tw-truncate tw-text-t-sm tw-text-t-primary lg:tw-text-t-base" title={file.file.name}>
                                                {file.file.name}
                                            </span>
                                            <ValidationStatus file={file} />
                                        </div>
                                        <span className="tw-text-t-xs tw-text-t-secondary lg:tw-text-t-sm">{size}</span>
                                    </div>
                                </div>
                            </TableCell>
                            {showProducts && (
                                <TableCell>
                                    {firstItem ? (
                                        <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                            {describeItem(firstItem)}
                                            {moreItems.length > 0 && (
                                                <span
                                                    className="tw-rounded tw-border tw-border-solid tw-border-primary tw-px-1 tw-text-t-xs lg:tw-text-t-sm"
                                                    title={moreItems.map(describeItem).join('\n')}
                                                >
                                                    +{moreItems.length}
                                                </span>
                                            )}
                                        </span>
                                    ) : EMPTY_VALUE}
                                </TableCell>
                            )}
                            {showAttributes && attributes.map((attribute) => (
                                <TableCell key={attribute.name} className="tw-whitespace-nowrap">{valueOf(attribute, file.values[attribute.name])}</TableCell>
                            ))}
                            {isEditable && (
                                <TableCell>
                                    <div className="tw-flex tw-justify-end tw-gap-1">
                                        {file.dimensions && onZoom && (
                                            <ButtonPanel
                                                isDisabled={false}
                                                icon={ArrowsPointingOutIcon}
                                                tooltip={t('DAM.UPLOAD.ZOOM')}
                                                aria-label={t('DAM.UPLOAD.ZOOM')}
                                                onClick={() => onZoom(file)}
                                            />
                                        )}
                                        <ButtonPanel
                                            isDisabled={false}
                                            icon={PencilIcon}
                                            tooltip={mode === 'link' ? t('DAM.UPLOAD.LINK_PRODUCT') : t('DAM.UPLOAD.EDIT_DATA')}
                                            aria-label={mode === 'link' ? t('DAM.UPLOAD.LINK_PRODUCT') : t('DAM.UPLOAD.EDIT_DATA')}
                                            onClick={() => onEdit?.(file)}
                                        />
                                        <ButtonPanel
                                            isDisabled={false}
                                            icon={TrashIcon}
                                            variant="destructive"
                                            tooltip={t('DAM.UPLOAD.DELETE_FILE')}
                                            aria-label={t('DAM.UPLOAD.DELETE_FILE')}
                                            onClick={() => onDelete?.(file)}
                                        />
                                    </div>
                                </TableCell>
                            )}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

export { DamUploadFileList };
export type { DamUploadFileListProps, DamUploadListMode };
